/**
 * Cadena OFIMA + recaudo para Plan Separe tras verificación de abonos (no usar en crear/editar pedido).
 */
import axiosInstance from '../../api/axiosInstance'
import ofimaSyncService from '../../api/ofimaSyncService'
import { isRemisionYaFacturadaMessage } from '../../utils/orderB2bFacturaAfterSync'
import { facturarRemision } from './ofimaRemisionFacturaService'

type PedidoEjecutarYConsultar = {
  Numero?: string
  NumeroOfima?: string | number
  Procesado?: boolean | number | string
  Mensaje?: string
}

const MSJ_SIN_REMISION_PLAN_SEPARE =
  'No se encontró remisión en OFIMA para el pedido Plan Separe'

/**
 * id_remision usable solo si es numérico y > 0.
 * 0, null, vacío o inválido no cuentan (hay que crear remisión).
 */
export function normalizeIdRemisionUsable(
  value?: string | number | null
): string {
  if (value === null || value === undefined) return ''
  const raw = String(value).trim().replace(/\.+$/, '')
  if (!raw) return ''
  const lower = raw.toLowerCase()
  if (lower === 'null' || lower === 'undefined' || lower === 'nan') return ''
  const n = Number(raw)
  if (!Number.isFinite(n) || n <= 0) return ''
  return raw
}

/** Cuando ejecutar-y-consultar responde 200 pero sin remisión usable (p. ej. Procesado false + Mensaje). */
function mensajeSinRemisionTrasEjecutarYConsultar(
  _orderId: string,
  pedidoMatch: PedidoEjecutarYConsultar | null | undefined
): string {
  const detalle =
    pedidoMatch && typeof pedidoMatch.Mensaje === 'string'
      ? pedidoMatch.Mensaje.trim()
      : ''
  if (detalle) {
    return `${MSJ_SIN_REMISION_PLAN_SEPARE} ("${detalle}")`
  }
  return MSJ_SIN_REMISION_PLAN_SEPARE
}

async function crearRemisionPlanSepare(orderId: string): Promise<string> {
  await ofimaSyncService.sendToOfima([orderId])
  await ofimaSyncService.ejecutarSPX()
  const consulta = await ofimaSyncService.ejecutarYConsultar([orderId])
  const pedidoOfima = Array.isArray(consulta?.pedidos)
    ? consulta.pedidos.find(
        (p: PedidoEjecutarYConsultar) => String(p.Numero) === orderId
      )
    : null
  const nrodcto = normalizeIdRemisionUsable(pedidoOfima?.NumeroOfima)
  if (!nrodcto) {
    throw new Error(mensajeSinRemisionTrasEjecutarYConsultar(orderId, pedidoOfima))
  }
  return nrodcto
}

/**
 * Verifica o crea factura FS sobre la remisión.
 * Si OFIMA indica que ya tiene factura, se considera verificado (OK).
 */
async function ensureFacturaPlanSepare(
  orderId: string,
  storeName: string,
  nrodcto: string
): Promise<void> {
  try {
    await facturarRemision('RE', nrodcto, {
      tipoFactura: 'FS',
      sglOrderId: orderId,
      idMarket: storeName || '',
      numeroPedido: orderId
    })
  } catch (err) {
    const msg = err instanceof Error ? err.message : String(err || '')
    if (isRemisionYaFacturadaMessage(msg)) {
      return
    }
    throw err
  }
}

/**
 * Prerrequisito obligatorio antes de abonar (cierre):
 * 1) Verificar/crear remisión
 * 2) Verificar/crear factura
 * Sin ambos, no se debe llamar a /recaudos/cierre.
 */
export async function ensureRemisionYFacturaPlanSepare(
  orderId: string,
  storeName: string,
  idRemisionExistente?: string | number | null
): Promise<{ nrodcto: string }> {
  // 0 / vacío / inválido → crear remisión; solo > 0 se reutiliza
  const existente = normalizeIdRemisionUsable(idRemisionExistente)
  const nrodcto = existente || (await crearRemisionPlanSepare(orderId))
  await ensureFacturaPlanSepare(orderId, storeName, nrodcto)
  return { nrodcto }
}

/** Compatibilidad: crea remisión si falta y siempre verifica/crea factura. */
export async function runOfimaChainBeforeFirstCierre(
  orderId: string,
  storeName: string
): Promise<{ nrodcto: string }> {
  return ensureRemisionYFacturaPlanSepare(orderId, storeName, null)
}

export interface RecaudoCierreAbonoBody {
  orderId: string
  paymentMethod: string
  nrodcto: string
  tipodcto: string
  abono: {
    valor: number
    metodo_pago: string
    id_transaccion: string | null
    id_pedido_pago?: number | null
  }
}

type RecaudoCierreResponse = {
  status?: string
  code?: string
  message?: string
  error?: string
  data?: {
    status?: string
    code?: string
    message?: string
    error?: string
  }
}

const NEGATIVE_STATUSES = ['error', 'failed', 'failure', 'rejected', 'not_applied', 'negative']

export async function postRecaudoCierre(body: RecaudoCierreAbonoBody): Promise<void> {
  const response = await axiosInstance.post('/ofima-sync/recaudos/cierre', body)
  const payload = (response?.data || {}) as RecaudoCierreResponse
  const status = (payload.status || payload.data?.status || '').toString().trim().toLowerCase()
  const code = (payload.code || payload.data?.code || '').toString().trim()
  const apiMessage =
    payload.message ||
    payload.data?.message ||
    payload.error ||
    payload.data?.error ||
    ''

  if (NEGATIVE_STATUSES.includes(status)) {
    throw new Error(apiMessage || 'No se pudo aplicar el abono (status negativo en /recaudos/cierre)')
  }

  if (code !== 'RECAUDO_APPLIED') {
    throw new Error(
      apiMessage ||
        `No se pudo aplicar el abono: respuesta inválida de /recaudos/cierre (code: ${code || 'sin código'})`
    )
  }
}

type PlanSeparePagoRow = {
  id?: number
  metodo_pago?: string
  valor?: number | string
  id_transaccion?: string | null
  forma_pago?: string
  plazo?: number
  pago_verificado?: number
  fecha_creacion?: string
}

function normalizePmLocal(value?: string | null): string {
  return (value ?? '').toString().trim().toLowerCase()
}

function getPagosFromDetail(detail: {
  pagos?: PlanSeparePagoRow[]
  extraDetails?: { pagos?: PlanSeparePagoRow[] }
}): PlanSeparePagoRow[] {
  const fromExtra = detail.extraDetails?.pagos
  const fromRoot = detail.pagos
  if (Array.isArray(fromExtra) && fromExtra.length > 0) return fromExtra
  if (Array.isArray(fromRoot) && fromRoot.length > 0) return fromRoot
  return []
}

function findPlanSepareBoldPagoIndex(
  pagos: PlanSeparePagoRow[],
  paymentId: string,
  pagoIndex?: number
): number {
  if (
    typeof pagoIndex === 'number' &&
    pagoIndex >= 0 &&
    pagoIndex < pagos.length &&
    normalizePmLocal(pagos[pagoIndex]?.metodo_pago) === 'bold'
  ) {
    return pagoIndex
  }
  const target = paymentId.trim().toLowerCase()
  // Incluir ya verificados: Bold confirm puede dejar pago_verificado=1 antes del cierre
  const byTx = pagos.findIndex(
    (p) =>
      normalizePmLocal(p.metodo_pago) === 'bold' &&
      (p.id_transaccion || '').toString().trim().toLowerCase() === target
  )
  if (byTx >= 0) return byTx
  return pagos.findIndex((p) => normalizePmLocal(p.metodo_pago) === 'bold')
}

export interface CompletePlanSepareBoldAbonoParams {
  token: string
  orderId: number
  storeName: string
  /** ID/link Bold del tramo que se acaba de confirmar */
  paymentId: string
  /** Índice del abono en pagos (si se conoce desde el listado/detalle) */
  pagoIndex?: number
}

/**
 * Tras confirmar Bold en plan_separe (Lista o Verificar Transacción):
 * 1) remisión solo si falta (vía ejecutar-y-consultar)
 * 2) factura solo si falta
 * 3) POST /ofima-sync/recaudos/cierre (el back marca pago_verificado / abonado)
 * 4) GET pedido para leer estado real
 *
 * No enviar PATCH de pago_verificado ni status=abonado desde el front:
 * si cierre falla, el pedido no debe quedar marcado como abonado/verificado aquí.
 */
export async function completePlanSepareBoldAbonoAfterConfirm(
  params: CompletePlanSepareBoldAbonoParams
): Promise<{ nrodcto: string; targetStatus: string }> {
  const { token, orderId, storeName, paymentId, pagoIndex } = params
  const authHeaders = {
    Authorization: `Bearer ${token}`,
    'Content-Type': 'application/json'
  }

  const latestRes = await axiosInstance.get(`/list/${orderId}`, { headers: authHeaders })
  const latest = latestRes.data as {
    id?: number
    status?: string
    store_name?: string
    id_remision?: string | number | null
    payment_method?: string
    pagos?: PlanSeparePagoRow[]
    extraDetails?: {
      payment_method?: string
      pagos?: PlanSeparePagoRow[]
      [key: string]: unknown
    }
  }

  const latestPagos = getPagosFromDetail(latest)
  const index = findPlanSepareBoldPagoIndex(latestPagos, paymentId, pagoIndex)
  if (index < 0) {
    throw new Error(`No se encontró abono Bold en el pedido #${orderId}`)
  }
  const latestPago = latestPagos[index]

  const { nrodcto } = await ensureRemisionYFacturaPlanSepare(
    String(orderId),
    (latest.store_name || storeName || '').trim(),
    latest.id_remision
  )

  const idPedidoPago =
    typeof latestPago.id === 'number' ? latestPago.id : null
  if (idPedidoPago === null) {
    throw new Error(`No se encontró id_pedido_pago para el abono Bold del pedido #${orderId}`)
  }

  const txCierre =
    (latestPago.id_transaccion || '').toString().trim() ||
    paymentId.trim()

  await postRecaudoCierre({
    orderId: String(orderId),
    paymentMethod: 'plan_separe',
    nrodcto,
    tipodcto: 'RE',
    abono: {
      valor: Math.round(Number(latestPago.valor) || 0),
      metodo_pago: 'bold',
      id_transaccion: txCierre || null,
      id_pedido_pago: idPedidoPago
    }
  })

  const afterCierreRes = await axiosInstance.get(`/list/${orderId}`, { headers: authHeaders })
  const afterCierre = afterCierreRes.data as {
    status?: string
    pagos?: PlanSeparePagoRow[]
    extraDetails?: { pagos?: PlanSeparePagoRow[] }
  }
  const afterPagos = getPagosFromDetail(afterCierre)
  const todosVerificados =
    afterPagos.length > 0 &&
    afterPagos.every((p) => Number(p.pago_verificado) === 1)
  const statusFromBack = (afterCierre.status || '').toString().trim().toLowerCase()
  const targetStatus =
    statusFromBack ||
    (todosVerificados ? 'abonado' : (latest.status || 'verify_transaction').toString().trim().toLowerCase())

  return { nrodcto, targetStatus }
}
